import { AppUser, HistoricalDataStatus, SystemFlags } from "../interfaces/IUsers";
import { ArchiveUserData } from "../interfaces/IArchiveData";
import { db } from "../App";
import { Gender } from "../interfaces/enums";
import { Participant } from "../interfaces/IParticipants";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";

/**
 * Sprawdza czy użytkownik ma dostępne dane historyczne do importu
 */
export const checkHistoricalDataAvailability = async (
  userEmail: string
): Promise<ArchiveUserData | null> => {
  try {
    const docRef = doc(db, "archiveData", userEmail);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data() as ArchiveUserData;
    }

    return null;
  } catch (error) {
    console.error("Błąd sprawdzania danych historycznych:", error);
    return null;
  }
};

/**
 * Sprawdza status danych historycznych użytkownika
 */
export const getHistoricalDataStatus = (user: AppUser): HistoricalDataStatus => {
  return user.systemFlags?.historicalDataStatus || HistoricalDataStatus.PENDING;
};

/**
 * Sprawdza czy użytkownik może importować dane historyczne
 */
export const canImportHistoricalData = (user: AppUser): boolean => {
  const status = getHistoricalDataStatus(user);
  return status === HistoricalDataStatus.PENDING || status === HistoricalDataStatus.SKIPPED;
};

/**
 * Aktualizuje status danych historycznych użytkownika
 */
export const updateHistoricalDataStatus = async (
  userId: string,
  status: HistoricalDataStatus
): Promise<void> => {
  try {
    const userRef = doc(db, "users", userId);
    const now = Timestamp.now();

    const systemFlags: SystemFlags = {
      historicalDataStatus: status,
      ...(status === HistoricalDataStatus.IMPORTED && { historicalDataImportedAt: now }),
      ...(status === HistoricalDataStatus.REJECTED && { historicalDataRejectedAt: now }),
    };

    await updateDoc(userRef, {
      systemFlags,
    });
  } catch (error) {
    console.error("Błąd aktualizacji statusu danych historycznych:", error);
    throw error;
  }
};

/**
 * Pobiera istniejących zawodników użytkownika
 */
const getExistingParticipants = async (userId: string): Promise<Participant[]> => {
  try {
    const participantsRef = collection(db, `users/${userId}/participants`);
    const q = query(participantsRef, where("isActive", "==", true));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    })) as unknown as Participant[];
  } catch (error) {
    console.error("Błąd pobierania istniejących zawodników:", error);
    return [];
  }
};

/**
 * Przekształca dane archiwalne na format Participant
 */
const transformArchiveParticipant = (archiveParticipant: any, userId: string): Participant => {
  // Mapowanie płci z formatu archiwalnego na enum
  let gender: Gender = Gender.FEMALE; // domyślnie
  if (
    archiveParticipant.gender === "MĘŻCZYZNA" ||
    archiveParticipant.gender === "MĘŻCZYŹNI" ||
    archiveParticipant.gender === "MALE"
  ) {
    gender = Gender.MALE;
  } else if (
    archiveParticipant.gender === "KOBIETA" ||
    archiveParticipant.gender === "KOBIETY" ||
    archiveParticipant.gender === "FEMALE"
  ) {
    gender = Gender.FEMALE;
  }

  // Parsowanie daty urodzenia
  let birthDate = new Date();
  if (archiveParticipant.birthDate) {
    const parsedDate = new Date(archiveParticipant.birthDate);
    if (!isNaN(parsedDate.getTime())) {
      birthDate = parsedDate;
    }
  }

  return {
    userId,
    firstName: archiveParticipant.firstName || "",
    lastName: archiveParticipant.lastName || "",
    birthDate,
    gender,
    isActive: true,
  };
};

/**
 * Sprawdza czy zawodnik już istnieje (po imieniu, nazwisku i dacie urodzenia)
 */
const participantExists = (
  newParticipant: Participant,
  existingParticipants: Participant[]
): boolean => {
  return existingParticipants.some((existing) => {
    // Konwertuj birthDate na Date jeśli to konieczne
    let existingBirthDate: Date;
    if (existing.birthDate instanceof Date) {
      existingBirthDate = existing.birthDate;
    } else if (existing.birthDate && typeof (existing.birthDate as any).toDate === "function") {
      // Firestore Timestamp
      existingBirthDate = (existing.birthDate as any).toDate();
    } else if (typeof existing.birthDate === "string") {
      existingBirthDate = new Date(existing.birthDate);
    } else {
      existingBirthDate = new Date();
    }

    const nameMatch =
      existing.firstName.toLowerCase() === newParticipant.firstName.toLowerCase() &&
      existing.lastName.toLowerCase() === newParticipant.lastName.toLowerCase();

    const dateMatch = existingBirthDate.getTime() === newParticipant.birthDate.getTime();

    return nameMatch && dateMatch;
  });
};

/**
 * Importuje dane historyczne użytkownika
 */
export const importHistoricalData = async (
  user: AppUser,
  archiveData: ArchiveUserData
): Promise<void> => {
  try {
    // 1. Pobierz istniejących zawodników użytkownika
    const existingParticipants = await getExistingParticipants(user.id);

    // 2. Przekształć i dodaj nowych zawodników
    let importedCount = 0;
    let skippedCount = 0;

    for (const archiveParticipant of archiveData.participants) {
      const newParticipant = transformArchiveParticipant(archiveParticipant, user.id);

      // Sprawdź czy zawodnik już istnieje
      if (participantExists(newParticipant, existingParticipants)) {
        skippedCount++;
        continue;
      }

      // Dodaj nowego zawodnika
      try {
        const docRef = await addDoc(
          collection(db, `users/${user.id}/participants`),
          newParticipant
        );
        importedCount++;
      } catch (error) {
        console.error(
          `Błąd dodawania zawodnika ${newParticipant.firstName} ${newParticipant.lastName}:`,
          error
        );
      }
    }

    // 3. Zaktualizuj status na IMPORTED
    await updateHistoricalDataStatus(user.id, HistoricalDataStatus.IMPORTED);

    // Rzuć błąd jeśli wszystkie zawodnicy już istnieją
    if (importedCount === 0 && skippedCount > 0) {
      throw new Error(
        `Wszyscy zawodnicy (${skippedCount}) już istnieją w Twojej bazie danych. Import nie był konieczny.`
      );
    }
  } catch (error) {
    console.error("Błąd importu danych historycznych:", error);
    throw error;
  }
};
